import styled from '@emotion/styled'
import { onFormatDate, UserPersona, YesNoIcon } from 'components'
import {
  ColumnRenderer,
  renderDate,
  renderDateFromTicks,
  renderDefault,
  renderEnum,
  renderTimeAgo,
  renderYesNoIcon,
} from 'components/EntityList'
import { groupSortByColumn } from 'components/EntityList/helpers'
import { UserPersonaById } from 'components/UserPersona/UserPersona'
import { Box } from 'grid'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { TFunction } from 'i18next'
import _ from 'lodash'
import {
  isEmpty,
  isNull,
  isNumber,
  isString,
  isUndefined,
  startsWith,
} from 'lodash-es'
import {
  AccomodationType,
  CarType,
  ExternalVehicle,
  FlightType,
  InternalVehicle,
  Proving,
  ProvingChange,
} from 'model'
import {
  CheckboxVisibility,
  DetailsList,
  DetailsListLayoutMode,
  IDetailsGroupDividerProps,
  IDetailsListProps,
  IGroup,
  IRenderFunction,
  PersonaSize,
  Stack,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { isBoolean } from 'util'
import { createFieldColumn } from 'views/helpers'
import { GroupHeader } from './GroupHeader'

const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/

const StyledDetailsList = styled(DetailsList)`
  .ms-DetailsRow-cell {
    color: ${({ theme }) => theme.palette.neutralPrimaryAlt};
  }
`

const StyledBox = styled(Box)`
  padding-top: 3px;
`

const createRenderKey: (proving: Proving, t: TFunction) => ColumnRenderer =
  (proving, t) => (item, i, props) => {
    const key = item[props!.fieldName!]
    if (item.path === '$.participants' && item.$key.length === 24) {
      return t('field.participant.label')
    } else if (_.startsWith(item.path, '$.participants')) {
      const participantId = item.path.split("'")[1]
      let person
      let personUnknown
      let personType
      let department
      let percentage
      let supplier
      let trackHotels
      let headOfProving
      let surName
      let firstName

      // tslint:disable-next-line: no-unused-expression
      proving &&
        proving.participants!.forEach((p) => {
          if (p._id === participantId) {
            if (p.unknownPerson && p.unknownPerson.isUnknown) {
              personUnknown = true
            }
            if (p.unknownPerson && p.unknownPerson.personType) {
              if (
                p.unknownPerson.personType === 'user' &&
                p.unknownPerson.firstName &&
                p.unknownPerson.surName
              ) {
                personType = 'user'
                firstName = p.unknownPerson.firstName
                surName = p.unknownPerson.surName
              }
              if (p.unknownPerson.personType === 'user') {
                personType = 'user'
              } else if (p.unknownPerson.personType === 'external') {
                personType = 'external'
              } else if (p.unknownPerson.personType === 'worker') {
                personType = 'worker'
                department = p.unknownPerson.department
                percentage = p.percentage
              } else if (p.unknownPerson.personType === 'supplier') {
                personType = 'supplier'
                firstName = p.unknownPerson.firstName
                surName = p.unknownPerson.surName
                supplier = p.unknownPerson.supplier
              }
            }
            person = p.person
          }
        })

      if (_.includes(item.path, 'trackHotels')) {
        trackHotels = 'Streckenhotel'
      }
      if (_.includes(item.path, 'headOfProving')) {
        headOfProving = 'Erprobungsleiter'
      }
      return (
        <Stack horizontal={true} tokens={{ childrenGap: 4 }}>
          <Stack.Item>
            {person && personUnknown !== true && (
              <UserPersona person={person} size={PersonaSize.size24} />
            )}
          </Stack.Item>
          <Stack.Item>
            <StyledBox>
              {personType === 'user' && firstName && surName
                ? `${surName}, ${firstName} `
                : ''}
              {personUnknown &&
                personType === 'user' &&
                t('model.proving.participant.participantUnknown')}
              {personUnknown &&
                personType === 'external' &&
                t('model.proving.participant.participantUnknown')}
              {personUnknown &&
                personType === 'worker' &&
                `${percentage}%, ${department}`}
              {personUnknown && personType === 'supplier' && supplier
                ? `${surName}, ${firstName} - ${supplier}`
                : personUnknown && personType === 'supplier'
                ? '[Unbekannter Lieferant]'
                : ''}
            </StyledBox>
          </Stack.Item>
          <Stack.Item>
            {headOfProving && <StyledBox>{headOfProving}</StyledBox>}
            {trackHotels && <StyledBox>{trackHotels}</StyledBox>}
            {!trackHotels && !headOfProving && (
              <StyledBox>
                {_.endsWith(item.path, '.rentalCar.dateTo') ? 'Mietwagen ' : ''}
                {_.endsWith(item.path, '.rentalCar.dateFrom')
                  ? 'Mietwagen '
                  : ''}
                {_.endsWith(item.path, '.accomodation.dateTo')
                  ? 'Unterkunft '
                  : ''}
                {_.endsWith(item.path, '.accomodation.dateFrom')
                  ? 'Unterkunft '
                  : ''}
                {_.endsWith(item.path, '].dateTo') ? 'Teilnahme ' : ''}
                {_.endsWith(item.path, '].dateFrom') ? 'Teilnahme ' : ''}
                {item.$key.length === 24
                  ? t('field.participant.label')
                  : t(`field.${key}.label`, { defaultValue: key })}
              </StyledBox>
            )}
          </Stack.Item>
        </Stack>
      )
    } else if (_.startsWith(item.path, '$.orderIdDistribution')) {
      return t('field.orderIdDistribution.label')
    } else if (
      _.includes(item.path, 'transport.returnTransport') ||
      _.includes(item.path, 'transport.outboundTransport') ||
      _.includes(item.path, 'materialsTransport.returnTransport') ||
      _.includes(item.path, 'materialsTransport.outboundTransport')
    ) {
      switch (item.$key) {
        case 'returnTransportSenderDate':
          return 'Rücktransport Beladedatum'
        case 'returnTransportSender':
          return 'Rücktransport Beladeort'
        case 'returnTransportSenderTime':
          return 'Rücktransport Beladeuhrzeit'
        case 'returnTransportRecipientDate':
          return 'Rücktransport Entladedatum'
        case 'returnTransportRecipient':
          return 'Rücktransport Entladeort'
        case 'returnTransportRecipientTime':
          return 'Rücktransport Entladeuhrzeit'
        case 'outboundTransportRecipientDate':
          return 'Hintransport Entladedatum'
        case 'outboundTransportRecipient':
          return 'Hintransport Entladeort'
        case 'outboundTransportRecipientTime':
          return 'Hintransport Entladeuhrzeit'
        case 'outboundTransportSenderDate':
          return 'Hintransport Beladedatum'
        case 'outboundTransportSender':
          return 'Hintransport Beladeort'
        case 'outboundTransportSenderTime':
          return 'Hintransport Beladeuhrzeit'
        default:
          return 'Transport'
      }
    } else if (
      _.startsWith(item.path, '$.vehicles') &&
      item.$key.length === 24
    ) {
      return t('field.vehicleId.label')
    } else if (_.startsWith(item.path, '$.vehicles')) {
      // Handle vehicle field names
      switch (item.$key) {
        case 'licensePlateType':
          return 'Kennzeichen'
        case 'label':
          return 'Bezeichnung'
        case 'licensePlate':
          return 'Kennzeichen'
        case 'vin':
          return 'Fahrgestellnummer'
        case 'model':
          return 'Fahrzeugmodell'
        case 'dateFrom':
          return 'Von'
        case 'dateTo':
          return 'Bis'
        case 'orderId':
          return 'Auftragsnummer'
        case 'unknown':
          return 'Fahrzeug unbekannt'
        default:
          return t(`field.${key}.label`, { defaultValue: key })
      }
    } else if (
      _.startsWith(item.path, '$.materials') &&
      item.$key.length === 24
    ) {
      return t('field.material.label')
    } else if (
      _.startsWith(item.path, '$.resourceBookings') &&
      item.$key.length === 24
    ) {
      return t('field.resourceBooking.label')
    } else if (
      _.startsWith(item.path, '$.resourceRequirements') &&
      _.includes(item.path, 'orderIdDistribution') &&
      item.$key.length === 24
    ) {
      return 'Ressourcenbedarf Auftragsnummern'
    } else if (
      _.startsWith(item.path, '$.resourceRequirements') &&
      item.$key.length === 24
    ) {
      return t('field.resourceRequirement.label')
    } else if (_.startsWith(item.path, '$.nmf')) {
      return 'Bedarfsmeldung'
    } else if (item.$key === 'flightType') {
      return t('field.flight.label')
    } else if (_.startsWith(item.path, '$.specialUsage')) {
      if (item.$key === 'bySaturday') {
        return 'Streckennutzung an Samstagen'
      } else if (item.$key === 'bySunday') {
        return 'Streckennutzung an Sonntagen'
      } else if (item.$key === 'byNight') {
        return 'Streckennutzung bei Nacht'
      } else {
        return 'Streckennutzung zu besonderen Zeiten'
      }
    } else {
      return t(`field.${key}.label`, { defaultValue: key })
    }
  }

const createRenderValue: (proving: Proving, t: TFunction) => ColumnRenderer =
  (proving, t) => (item, i, props) => {
    if (
      _.startsWith(item.path, '$.participants') &&
      !_.includes(item.path, 'date') &&
      !_.includes(item.path, 'orderId') &&
      !_.includes(item.path, 'accomodation') &&
      !_.includes(item.path, 'booking') &&
      !_.includes(item.path, 'rentalCar') &&
      !_.includes(item.path, 'flight') &&
      !_.includes(item.path, 'ferry') &&
      !_.includes(item.path, 'trackHotel') &&
      !_.includes(item.path, 'headOfProving') &&
      !_.includes(item.path, 'nordschleife') &&
      item.type !== 'REMOVE' &&
      props &&
      props.key === 'value'
    ) {
      if (
        (item.value.unknownPerson &&
          item.value.unknownPerson.isUnknown === true &&
          item.value.unknownPerson.personType === 'user') ||
        (item.value.unknownPerson &&
          item.value.unknownPerson.isUnknown === true &&
          item.value.unknownPerson.personType === 'external')
      ) {
        return t('model.proving.participant.participantUnknown')
        // Manueller User
      } else if (
        (!item.value.person &&
          item.value.unknownPerson &&
          item.value.unknownPerson.isUnknown === false &&
          item.value.unknownPerson.personType === 'user') ||
        (!item.value.person &&
          item.value.unknownPerson &&
          item.value.unknownPerson.isUnknown === false &&
          item.value.unknownPerson.personType === 'external')
      ) {
        let completePerson: any

        // tslint:disable-next-line: no-unused-expression
        proving &&
          proving.participants &&
          proving.participants.forEach((p: any) => {
            if (item.$key === p._id) {
              completePerson = p
            }
          })

        return (
          <>
            <Box>
              {item.value.unknownPerson.surName},{' '}
              {item.value.unknownPerson.firstName}
            </Box>
            {completePerson && completePerson.dateFrom && (
              <Box>Von: {onFormatDate(new Date(completePerson.dateFrom))}</Box>
            )}
            {completePerson && completePerson.dateTo && (
              <Box>Bis: {onFormatDate(new Date(completePerson.dateTo))}</Box>
            )}
            {completePerson && completePerson.orderId && (
              <Box>Auftragsnummer: {completePerson.orderId}</Box>
            )}
            {completePerson &&
              completePerson.accomodation &&
              completePerson.accomodation.accomodationType &&
              completePerson.accomodation.accomodationType ===
                AccomodationType.AccomodationWithPreference && (
                <Box>
                  Unterkunft: {completePerson.accomodation.accomodation.title},{' '}
                  {onFormatDate(new Date(completePerson.accomodation.dateFrom))}{' '}
                  - {onFormatDate(new Date(completePerson.accomodation.dateTo))}
                </Box>
              )}
            {completePerson &&
              completePerson.flight &&
              completePerson.flight.flightType &&
              completePerson.flight.flightType !== FlightType.NoFlight && (
                <Box>
                  Flug:{' '}
                  {t(
                    `enum.flight.flightType.${completePerson.flight.flightType}`
                  )}
                </Box>
              )}
            {completePerson &&
              completePerson.rentalCar &&
              completePerson.rentalCar.carType &&
              completePerson.rentalCar.carType !== CarType.None && (
                <Box>
                  Mietwagen:{' '}
                  {t(`enum.rentalCar.${completePerson.rentalCar.carType}`)},{' '}
                  {onFormatDate(new Date(completePerson.rentalCar.dateFrom))} -{' '}
                  {onFormatDate(new Date(completePerson.rentalCar.dateTo))}
                </Box>
              )}
          </>
        )
      } else if (
        item.value.unknownPerson &&
        item.value.unknownPerson.personType === 'worker'
      ) {
        return (
          item.value.percentage + '%, ' + item.value.unknownPerson.department
        )
      } else if (
        item.value.unknownPerson &&
        item.value.unknownPerson.personType === 'supplier'
      ) {
        return item.value.unknownPerson.supplier
          ? `${item.value.unknownPerson.surName}, ${item.value.unknownPerson.firstName} - ${item.value.unknownPerson.supplier}`
          : '[Lieferant/Sonstige noch unbekannt]'
      } else if (
        item.path === '$.participants' &&
        props &&
        props.key.length === 24
      ) {
        return 'Teilnehmer'
      } else {
        let person
        let completePerson: any

        // tslint:disable-next-line: no-unused-expression
        proving &&
          proving.participants &&
          proving.participants.forEach((p: any) => {
            if (
              (p.person && p.person._id === item.value.person) ||
              (p.person && p.person._id === item.value)
            ) {
              person = p.person
              completePerson = p
            }
          })
        return (
          <>
            <UserPersona person={person as any} size={PersonaSize.size24} />
            {completePerson && completePerson.dateFrom && (
              <Box>Von: {onFormatDate(new Date(completePerson.dateFrom))}</Box>
            )}
            {completePerson && completePerson.dateTo && (
              <Box>Bis: {onFormatDate(new Date(completePerson.dateTo))}</Box>
            )}
            {completePerson && completePerson.orderId && (
              <Box>Auftragsnummer: {completePerson.orderId}</Box>
            )}
            {completePerson &&
              completePerson.accomodation &&
              completePerson.accomodation.accomodationType &&
              completePerson.accomodation.accomodationType ===
                AccomodationType.AccomodationWithPreference && (
                <Box>
                  Unterkunft: {completePerson.accomodation.accomodation.title},{' '}
                  {onFormatDate(new Date(completePerson.accomodation.dateFrom))}{' '}
                  - {onFormatDate(new Date(completePerson.accomodation.dateTo))}
                </Box>
              )}
            {completePerson &&
              completePerson.flight &&
              completePerson.flight.flightType &&
              completePerson.flight.flightType !== FlightType.NoFlight && (
                <Box>
                  Flug:{' '}
                  {t(
                    `enum.flight.flightType.${completePerson.flight.flightType}`
                  )}
                </Box>
              )}
            {completePerson &&
              completePerson.rentalCar &&
              completePerson.rentalCar.carType &&
              completePerson.rentalCar.carType !== CarType.None && (
                <Box>
                  Mietwagen:{' '}
                  {t(`enum.rentalCar.${completePerson.rentalCar.carType}`)},{' '}
                  {onFormatDate(new Date(completePerson.rentalCar.dateFrom))} -{' '}
                  {onFormatDate(new Date(completePerson.rentalCar.dateTo))}
                </Box>
              )}
          </>
        )
      }
    } else if (_.includes(item.path, 'nordschleife')) {
      return renderYesNoIcon(item, i, props)
    } else if (
      _.includes(item.path, 'flight') &&
      (item.$key === 'departure' ||
        item.$key === 'returnData' ||
        item.$key === 'outboundData')
    ) {
      if (props && props.key === 'value' && item.$key === 'departure') {
        if (item.value) {
          const departure = onFormatDate(new Date(item.value))
          if (departure === 'Invalid Date') {
            return renderDateFromTicks(item, i, props)
          }
          if (departure === 'NaN.NaN.NaN') {
            return '---'
          }
          return departure
        } else {
          return '---'
        }
      }
      if (props && props.key === 'oldValue' && item.$key === 'departure') {
        if (item.oldValue) {
          const departure = onFormatDate(new Date(item.oldValue))
          if (departure === 'Invalid Date') {
            return renderDateFromTicks(item, i, props)
          }
          if (departure === 'NaN.NaN.NaN') {
            return '---'
          }
          return departure
        } else {
          return '---'
        }
      }
      if (props && props.key === 'value' && item.$key === 'returnData') {
        if (item.value) {
          const returnData = onFormatDate(new Date(item.value.departure))
          if (returnData === 'Invalid Date') {
            return renderDateFromTicks(item, i, props)
          }
          if (returnData === 'NaN.NaN.NaN') {
            return '---'
          }
          return returnData
        } else {
          return '---'
        }
      }
      if (props && props.key === 'oldValue' && item.$key === 'returnData') {
        if (item.oldValue) {
          const returnData = onFormatDate(new Date(item.oldValue.departure))
          if (returnData === 'Invalid Date') {
            return renderDateFromTicks(item, i, props)
          }
          if (returnData === 'NaN.NaN.NaN') {
            return '---'
          }
          return returnData
        } else {
          return '---'
        }
      }
      if (props && props.key === 'value' && item.$key === 'outboundData') {
        if (item.value) {
          const outboundData = onFormatDate(new Date(item.value.departure))
          if (outboundData === 'Invalid Date') {
            return renderDateFromTicks(item, i, props)
          }
          if (outboundData === 'NaN.NaN.NaN') {
            return '---'
          }
          return outboundData
        } else {
          return '---'
        }
      }
      if (props && props.key === 'oldValue' && item.$key === 'outboundData') {
        if (item.oldValue) {
          const outboundData = onFormatDate(new Date(item.oldValue.departure))
          if (outboundData === 'Invalid Date') {
            return renderDateFromTicks(item, i, props)
          }
          if (outboundData === 'NaN.NaN.NaN') {
            return '---'
          }
          return outboundData
        } else {
          return '---'
        }
      }
    } else if (
      _.includes(item.path, 'accomodation') &&
      item.$key === 'accomodation'
    ) {
      if (props && props.key === 'value') {
        let accomodation = '---'
        // tslint:disable-next-line: no-unused-expression
        proving &&
          proving.participants &&
          proving.participants.forEach((p) => {
            if (
              p.accomodation &&
              p.accomodation.accomodation &&
              p.accomodation.accomodation._id === item.value
            ) {
              accomodation = p.accomodation.accomodation.title as any
            }
          })
        return item.type !== 'REMOVE'
          ? accomodation
          : t('enum.ProvingChangeOperation.REMOVE')
      }
      if (props && props.key === 'oldValue') {
        if (
          item.oldValue === null ||
          (item.oldValue === undefined && item.type !== 'REMOVE')
        ) {
          return '---'
        } else {
          let accomodation = '---'
          // tslint:disable-next-line: no-unused-expression
          proving &&
            proving.participants &&
            proving.participants.forEach((p) => {
              if (
                p.accomodation &&
                p.accomodation.accomodation &&
                p.accomodation.accomodation._id === item.oldValue
              ) {
                accomodation = p.accomodation.accomodation.title as any
              }
            })

          return accomodation
        }
      }
    } else if (
      _.includes(item.path, 'person') &&
      props &&
      props.key === 'oldValue' &&
      item.oldValue &&
      item.oldValue.length === 36
    ) {
      return (
        <UserPersonaById personId={item.oldValue} size={PersonaSize.size24} />
      )
    } else if (
      _.includes(item.path, 'person') &&
      props &&
      props.key === 'oldValue' &&
      item.oldValue &&
      item.oldValue.length <= 8
    ) {
      return (
        <UserPersonaById personId={item.oldValue} size={PersonaSize.size24} />
      )
    } else if (
      _.includes(item.path, 'trackHotels.outward') &&
      !_.includes(item.path, 'booking') &&
      item.$key !== 'dateFrom' &&
      item.$key !== 'dateTo'
    ) {
      if (props && props.key === 'oldValue') {
        if (item.oldValue === null || item.oldValue === undefined) {
          return '---'
        } else if (_.includes(item.path, 'dateFrom')) {
          const dateFrom = new Date(
            Date.parse(item.oldValue)
          ).toLocaleDateString()
          return dateFrom
        } else if (_.includes(item.path, 'dateTo')) {
          const dateTo = new Date(
            Date.parse(item.oldValue)
          ).toLocaleDateString()
          return dateTo
        } else if (_.includes(item.path, 'note')) {
          const note = item.oldValue
          return note.length > 0 ? note : '---'
        } else if (item.oldValue) {
          return 'Hoteländerung'
        }
      } else if (props && props.key === 'value' && item.$key === 'dateFrom') {
        const dateFrom = new Date(Date.parse(item.value)).toLocaleDateString()
        return dateFrom
      } else if (props && props.key === 'value' && item.$key === 'dateTo') {
        const dateTo = new Date(Date.parse(item.value)).toLocaleDateString()
        return dateTo
      } else if (props && props.key === 'value' && item.$key === 'note') {
        const note = item.value
        return note
      } else {
        let outwardHotel = ''
        if (item.value?.hotel) {
          proving &&
            proving.participants &&
            proving.participants.forEach((p) => {
              // tslint:disable-next-line: no-unused-expression
              p.trackHotels &&
                p.trackHotels.outward &&
                p.trackHotels.outward.forEach((o: any) => {
                  if (o?.hotel && o?.hotel._id === item.value?.hotel) {
                    outwardHotel = o?.hotel && o?.hotel.title
                  }
                })
            })
        } else if (item.value?.length === 24) {
          proving &&
            proving.participants &&
            proving.participants.forEach((p) => {
              // tslint:disable-next-line: no-unused-expression
              p.trackHotels &&
                p.trackHotels.outward &&
                p.trackHotels.outward.forEach((o: any) => {
                  if (o?.hotel && o?.hotel._id === item.value) {
                    outwardHotel = o?.hotel && o?.hotel.title
                  }
                })
            })
          return outwardHotel
        }
        const dateFrom = item?.value?.dateFrom
          ? new Date(Date.parse(item.value.dateFrom)).toLocaleDateString()
          : '---'
        const dateTo = item?.value?.dateTo
          ? new Date(Date.parse(item.value.dateTo)).toLocaleDateString()
          : '---'
        if (outwardHotel && outwardHotel === '---') {
          return '---'
        }

        return `${outwardHotel}: ${_.replace(
          dateFrom,
          /\//g,
          '.'
        )} - ${_.replace(dateTo, /\//g, '.')} ${
          item?.value?.note ? item.value.note : ''
        }`
      }
    } else if (
      _.includes(item?.path, 'trackHotels.return') &&
      !_.includes(item?.path, 'booking') &&
      item?.$key !== 'dateFrom' &&
      item?.$key !== 'dateTo'
    ) {
      if (props && props.key === 'oldValue') {
        if (item.oldValue === null || item.oldValue === undefined) {
          return '---'
        }
      } else if (item.value === null) {
        return '---'
      } else {
        let returnHotel: any = ''

        proving &&
          proving.participants &&
          proving.participants.forEach((p) => {
            // tslint:disable-next-line: no-unused-expression
            p.trackHotels &&
              p.trackHotels.return &&
              p.trackHotels.return.forEach((o: any) => {
                if (o?.hotel && o?.hotel._id === item.value?.hotel) {
                  returnHotel = o?.hotel && o?.hotel.title
                }
              })
          })
        const dateFrom = item.value.dateFrom
          ? new Date(Date.parse(item.value.dateFrom)).toLocaleDateString()
          : '---'
        const dateTo = item.value.dateTo
          ? new Date(Date.parse(item.value.dateTo)).toLocaleDateString()
          : '---'
        const note = item.value && item.value.note ? item.value.note : ''
        if (returnHotel && returnHotel === '---') {
          return '---'
        }
        return `${returnHotel}: ${_.replace(
          dateFrom,
          /\//g,
          '.'
        )} - ${_.replace(dateTo, /\//g, '.')} ${note}`
      }
    } else if (
      _.includes(item.path, 'isHeadOfProving') &&
      props &&
      props.key === 'value'
    ) {
      return (
        <Box px={1}>
          <YesNoIcon value={item.value} />
        </Box>
      )
    } else if (
      _.includes(item.path, 'isHeadOfProving') &&
      props &&
      props.key === 'oldValue'
    ) {
      return (
        <Box px={1}>
          <YesNoIcon value={item.oldValue} />
        </Box>
      )
    } else if (_.includes(item.path, 'ferry')) {
      if (props && props.key === 'oldValue') {
        if (item.oldValue === null || item.oldValue === undefined) {
          return '---'
        }
      } else if (props && props.key === 'value' && item.$key === 'dateFrom') {
        const dateFrom = new Date(Date.parse(item.value)).toLocaleDateString()

        return dateFrom
      } else if (props && props.key === 'value' && item.$key === 'dateTo') {
        const dateTo = item.value
        new Date(Date.parse(item.value)).toLocaleDateString()

        return dateTo
      } else if (props && props.key === 'value' && item.$key === 'timeFrom') {
        const timeFrom = item.value
        return `${timeFrom} Uhr`
      } else if (props && props.key === 'value' && item.$key === 'timeTo') {
        const timeTo = item.value
        return `${timeTo} Uhr`
      }
      if (_.includes(item.path, 'ferry.bookingStatus')) {
        if (props && props.key === 'oldValue') {
          return renderEnum(t, 'bookingStatus')(item, i, props)
        } else {
          return renderEnum(t, 'bookingStatus')(item, i, props)
        }
      } else if (item.value === null) {
        return '---'
      } else {
        let ferry: any = ''
        // tslint:disable-next-line: no-unused-expression
        proving &&
          proving.participants &&
          proving.participants.forEach((p) => {
            if (
              p.ferry &&
              p.ferry.outward &&
              p.ferry.outward._id === item.value
            ) {
              ferry = p.ferry.outward.title
            } else if (
              p.ferry &&
              p.ferry.return &&
              p.ferry.return._id === item.value
            ) {
              ferry = p.ferry.return.title
            }
          })
        return ferry
      }
    } else if (
      _.startsWith(item.path, '$.vehicles') &&
      !_.includes(item.path, 'model') &&
      !_.includes(item.path, 'label') &&
      !_.includes(item.path, 'date') &&
      !_.includes(item.path, 'bookingStatus') &&
      !_.includes(item.path, 'transport') &&
      !_.includes(item.path, 'orderId') &&
      !_.includes(item.path, 'licensePlate') &&
      !_.includes(item.path, 'vin') &&
      item.type !== 'REMOVE' &&
      props &&
      props.key === 'value'
    ) {
      const vehicle: any =
        proving &&
        proving.vehicles &&
        proving.vehicles.find(
          (vehicles: any) => vehicles._id === item.value._id
        )

      if (vehicle && vehicle.type === 'external') {
        return (
          <>
            Fremdfahrzeug:{' '}
            {vehicle && vehicle.labelExternal ? vehicle.labelExternal : ''}
            <br />
            {vehicle.licensePlate && (
              <>
                Kennzeichen: {vehicle.licensePlate ? vehicle.licensePlate : ''}
                <br />
              </>
            )}
            {vehicle.vin && (
              <>
                Fahrgestellnummer: {vehicle.vin ? vehicle.vin : ''}
                <br />
              </>
            )}
            Von:{' '}
            {vehicle.dateFrom ? onFormatDate(new Date(vehicle.dateFrom)) : ''}
            <br />
            Bis: {vehicle.dateTo ? onFormatDate(new Date(vehicle.dateTo)) : ''}
            <br />
            Auftragsnummer: {vehicle.orderId ? vehicle.orderId : ''}
            <br />
            Transport:{' '}
            {vehicle && vehicle.transport && vehicle.transport.transportType
              ? t(`enum.transportType.${vehicle.transport.transportType}`)
              : 'Kein Transport'}
          </>
        )
      } else if (vehicle && vehicle.unknown) {
        return (
          <>
            {t('model.proving.vehicle.vehicleUnknown.long')}
            <br />
            Model: {vehicle.model ? vehicle.model : ''}
          </>
        )
      } else {
        return (
          <>
            Model: {vehicle && vehicle.model ? vehicle.model : ''}
            <br />
            Bezeichnung: {vehicle && vehicle.label ? vehicle.label : ''}
            <br />
            {vehicle && vehicle.licensePlate && (
              <>
                Kennzeichen:{' '}
                {vehicle && vehicle.licensePlate ? vehicle.licensePlate : ''}
                <br />
              </>
            )}
            {vehicle && vehicle.vin && (
              <>
                Fahrgestellnummer: {vehicle && vehicle.vin ? vehicle.vin : ''}
                <br />
              </>
            )}
            Von:{' '}
            {vehicle && vehicle.dateFrom
              ? onFormatDate(new Date(vehicle.dateFrom))
              : ''}
            <br />
            Bis:{' '}
            {vehicle && vehicle.dateTo
              ? onFormatDate(new Date(vehicle.dateTo))
              : ''}
            <br />
            Auftragsnummer: {vehicle && vehicle.orderId ? vehicle.orderId : ''}
            <br />
            Transport:{' '}
            {vehicle && vehicle.transport && vehicle.transport.transportType
              ? t(`enum.transportType.${vehicle.transport.transportType}`)
              : 'Kein Transport'}
          </>
        )
      }
    } else if (
      _.startsWith(item.path, '$.resourceRequirements') &&
      !_.includes(item.path, 'amount') &&
      !_.includes(item.path, 'date') &&
      !_.includes(item.path, 'orderId') &&
      !_.includes(item.path, 'status') &&
      !_.includes(item.path, 'percentage') &&
      !_.includes(item.path, 'note') &&
      item.type !== 'REMOVE' &&
      props &&
      props.key === 'value'
    ) {
      return (
        <>
          Typ:{' '}
          {t(
            `enum.resourceRequirementType.${item.value.resourceRequirementType}`
          )}
          <br />
          Anzahl / Menge / h: {item.value.amount}
          <br />
          Von: {onFormatDate(new Date(Date.parse(item.value.dateFrom)))}
          <br />
          Bis: {onFormatDate(new Date(Date.parse(item.value.dateTo)))}
        </>
      )
      // } else if (
      //   _.startsWith(item.path, '$.resourceRequirements') &&
      //   _.includes(item.path, 'orderIdDistribution') &&
      //   props &&
      //   props.key === 'value'
      // ) {
      //   return 'test'
    } else if (
      (item.path as string).match(
        /^\$\.resourceRequirements.*orderIdDistribution$/
      ) &&
      props &&
      props.key === 'value'
    ) {
      return (
        <>
          Auftragsnummer: {item.value.orderId}
          <br />
          Anteil: {item.value.percentage} %
        </>
      )
    } else if (
      (item.path as string).match(
        /^\$\.resourceRequirements.*orderIdDistribution\[\?\(@\._id='.{24}'\)\]$/
      ) &&
      props &&
      props.key === 'oldValue'
    ) {
      return (
        <>
          Auftragsnummer: {item.value.orderId}
          <br />
          Anteil: {item.value.percentage} %
        </>
      )
    } else if (_.startsWith(item.path, '$.nmf') && item.type !== 'REMOVE') {
      if (props && props.key === 'value') {
        return renderYesNoIcon(item, i, props)
      }
      if (props && props.key === 'oldValue') {
        return renderYesNoIcon(item, i, props)
      }
    } else if (
      _.startsWith(item.path, '$.specialUsage') &&
      item.type !== 'REMOVE'
    ) {
      return renderYesNoIcon(item, i, props)
    } else if (
      _.startsWith(item.path, '$.resourceBookings') &&
      !_.includes(item.path, 'date') &&
      !_.includes(item.path, 'orderId') &&
      !_.includes(item.path, 'usageTime') &&
      item.type !== 'REMOVE' &&
      props &&
      props.key === 'value'
    ) {
      if (_.includes(item.path, 'participant')) {
        const participant =
          proving.participants &&
          proving.participants.find((p: any) => p._id === item.value)
        return participant ? (
          <UserPersona
            person={participant.person as any}
            size={PersonaSize.size24}
          />
        ) : (
          '---'
        )
      } else {
        const resourceBooking =
          proving &&
          proving.resourceBookings &&
          proving.resourceBookings.find(
            (resBookings: any) =>
              resBookings.resource._id === item.value.resource
          )
        return resourceBooking ? resourceBooking.resource.title : '---'
      }
    } else if (
      _.startsWith(item.path, '$.materials') &&
      !_.includes(item.path, 'amount') &&
      !_.includes(item.path, 'orderId') &&
      !_.includes(item.path, 'transport') &&
      !_.includes(item.path, 'annotation') &&
      !_.includes(item.path, 'materialsTransport') &&
      item.type !== 'REMOVE' &&
      props &&
      props.key === 'value'
    ) {
      const label =
        proving.materials &&
        proving.materials.find(
          (materials: any) =>
            materials &&
            materials.material &&
            item.value &&
            item.value.material &&
            materials.material._id === item.value.material
        )
      if (label && label.material) {
        return (
          <>
            Bezeichnung: {label.material.label}
            <br />
            Gefahrengut: {label.isDangerousGood ? 'Ja' : 'Nein'}
            <br />
            {label.isDangerousGood && (
              <>
                UN Nummer: {label.dangerousGood?.unNumber || '---'}
                <br />
              </>
            )}
            von: {onFormatDate(new Date(label.dateFrom))}
            <br />
            bis: {onFormatDate(new Date(label.dateTo))}
            <br />
            Anzahl: {label.amount}
            <br />
            Auftragsnummer: {label.orderId}
            <br />
            Transport:{' '}
            {t(
              `enum.transportType.${
                label.transport && label.transport.transportType
              }`
            )}
            <br />
            Anmerkung: {label.annotation || '---'}
          </>
        )
      } else {
        return '---'
      }
      // return label ? label.material.label : '---'
    } else if (
      _.startsWith(item.path, '$.orderIdDistribution') &&
      !_.includes(item.path, 'percentage') &&
      props &&
      props.key === 'value'
    ) {
      return `${item.value.orderId}, ${item.value.percentage} %`
    } else if (
      (_.endsWith(item.path, 'TransportSender') &&
        item.type !== 'REMOVE' &&
        props &&
        props.key === 'value') ||
      (_.endsWith(item.path, 'TransportRecipient') &&
        item.type !== 'REMOVE' &&
        props &&
        props.key === 'value')
    ) {
      if (item.$key === 'outboundTransportRecipient') {
        let name: any
        if (_.includes(item.path, 'materialsTransport')) {
          name =
            proving &&
            proving.materialsTransport &&
            proving.materialsTransport.outboundTransportRecipient &&
            `${proving.materialsTransport.outboundTransportRecipient.name}, ${proving.materialsTransport.outboundTransportRecipient.place}`
        } else {
          // tslint:disable-next-line: no-unused-expression
          proving &&
            proving.vehicles &&
            proving.vehicles.forEach((v) => {
              if (
                v.transport &&
                v.transport.outboundTransportRecipient &&
                v.transport.outboundTransportRecipient._id === item.value
              ) {
                name = `${v.transport.outboundTransportRecipient.name}, ${v.transport.outboundTransportRecipient.place}`
              }
            })
        }
        return name ? name : item.oldValue !== null ? 'Entfernt' : '---'
      } else if (item.$key === 'outboundTransportSender') {
        let name: any
        if (_.includes(item.path, 'materialsTransport')) {
          name =
            proving &&
            proving.materialsTransport &&
            proving.materialsTransport.outboundTransportSender &&
            `${proving.materialsTransport.outboundTransportSender.name}, ${proving.materialsTransport.outboundTransportSender.place}`
        } else {
          // tslint:disable-next-line: no-unused-expression
          proving &&
            proving.vehicles &&
            proving.vehicles.forEach((v) => {
              if (
                v.transport &&
                v.transport.outboundTransportSender &&
                v.transport.outboundTransportSender._id === item.value
              ) {
                name = `${v.transport.outboundTransportSender.name}, ${v.transport.outboundTransportSender.place}`
              }
            })
        }
        return name ? name : item.oldValue !== null ? 'Entfernt' : '---'
      } else if (item.$key === 'returnTransportRecipient') {
        let name: any
        if (_.includes(item.path, 'materialsTransport')) {
          name =
            proving &&
            proving.materialsTransport &&
            proving.materialsTransport.returnTransportRecipient &&
            `${proving.materialsTransport.returnTransportRecipient.name}, ${proving.materialsTransport.returnTransportRecipient.place}`
        } else {
          // tslint:disable-next-line: no-unused-expression
          proving &&
            proving.vehicles &&
            proving.vehicles.forEach((v) => {
              if (
                v.transport &&
                v.transport.returnTransportRecipient &&
                v.transport.returnTransportRecipient._id === item.value
              ) {
                name = `${v.transport.returnTransportRecipient.name}, ${v.transport.returnTransportRecipient.place}`
              }
            })
        }
        return name ? name : item.oldValue !== null ? 'Entfernt' : '---'
      } else if (item.$key === 'returnTransportSender') {
        let name: any
        if (_.includes(item.path, 'materialsTransport')) {
          name =
            proving &&
            proving.materialsTransport &&
            proving.materialsTransport.returnTransportSender &&
            `${proving.materialsTransport.returnTransportSender.name}, ${proving.materialsTransport.returnTransportSender.place}`
        } else {
          // tslint:disable-next-line: no-unused-expression
          proving &&
            proving.vehicles &&
            proving.vehicles.forEach((v) => {
              if (
                v.transport &&
                v.transport.returnTransportSender &&
                v.transport.returnTransportSender._id === item.value
              ) {
                name = `${v.transport.returnTransportSender.name}, ${v.transport.returnTransportSender.place}`
              }
            })
        }
        return name ? name : item.oldValue !== null ? 'Entfernt' : '---'
      }
    } else if (
      (_.endsWith(item.path, 'TransportSender') &&
        item.type !== 'REMOVE' &&
        props &&
        props.key === 'oldValue') ||
      (_.endsWith(item.path, 'TransportRecipient') &&
        item.type !== 'REMOVE' &&
        props &&
        props.key === 'oldValue')
    ) {
      if (item.$key === 'outboundTransportRecipient') {
        let name: any
        if (_.includes(item.path, 'materialsTransport')) {
          name =
            proving &&
            proving.materialsTransport &&
            proving.materialsTransport.outboundTransportRecipient &&
            `${proving.materialsTransport.outboundTransportRecipient.name}, ${proving.materialsTransport.outboundTransportRecipient.place}`
        } else {
          // tslint:disable-next-line: no-unused-expression
          proving &&
            proving.vehicles &&
            proving.vehicles.forEach((v) => {
              if (
                v.transport &&
                v.transport.outboundTransportRecipient &&
                v.transport.outboundTransportRecipient._id === item.oldValue
              ) {
                name = `${v.transport.outboundTransportRecipient.name}, ${v.transport.outboundTransportRecipient.place}`
              }
            })
        }
        return name ? name : '---'
      } else if (item.$key === 'outboundTransportSender') {
        let name: any
        if (_.includes(item.path, 'materialsTransport')) {
          name =
            proving &&
            proving.materialsTransport &&
            proving.materialsTransport.outboundTransportSender &&
            `${proving.materialsTransport.outboundTransportSender.name}, ${proving.materialsTransport.outboundTransportSender.place}`
        } else {
          // tslint:disable-next-line: no-unused-expression
          proving &&
            proving.vehicles &&
            proving.vehicles.forEach((v) => {
              if (
                v.transport &&
                v.transport.outboundTransportSender &&
                v.transport.outboundTransportSender._id === item.oldValue
              ) {
                name = `${v.transport.outboundTransportSender.name}, ${v.transport.outboundTransportSender.place}`
              }
            })
        }
        return name ? name : '---'
      } else if (item.$key === 'returnTransportRecipient') {
        let name: any
        if (_.includes(item.path, 'materialsTransport')) {
          name =
            proving &&
            proving.materialsTransport &&
            proving.materialsTransport.returnTransportRecipient &&
            `${proving.materialsTransport.returnTransportRecipient.name}, ${proving.materialsTransport.returnTransportRecipient.place}`
        } else {
          // tslint:disable-next-line: no-unused-expression
          proving &&
            proving.vehicles &&
            proving.vehicles.forEach((v) => {
              if (
                v.transport &&
                v.transport.returnTransportRecipient &&
                v.transport.returnTransportRecipient._id === item.oldValue
              ) {
                name = `${v.transport.returnTransportRecipient.name}, ${v.transport.returnTransportRecipient.place}`
              }
            })
        }
        return name ? name : '---'
      } else if (item.$key === 'returnTransportSender') {
        let name: any
        if (_.includes(item.path, 'materialsTransport')) {
          name =
            proving &&
            proving.materialsTransport &&
            proving.materialsTransport.returnTransportSender &&
            `${proving.materialsTransport.returnTransportSender.name}, ${proving.materialsTransport.returnTransportSender.place}`
        } else {
          // tslint:disable-next-line: no-unused-expression
          proving &&
            proving.vehicles &&
            proving.vehicles.forEach((v) => {
              if (
                v.transport &&
                v.transport.returnTransportSender &&
                v.transport.returnTransportSender._id === item.oldValue
              ) {
                name = `${v.transport.returnTransportSender.name}, ${v.transport.returnTransportSender.place}`
              }
            })
        }
        return name ? name : '---'
      }
    } else if (
      (_.endsWith(item.path, 'TransportSenderDate') ||
        _.endsWith(item.path, 'TransportRecipientDate')) &&
      item.type === 'ADD' &&
      item.status === 'PENDING' &&
      props &&
      props.key === 'oldValue'
    ) {
      return item.oldValue ? renderDate(item, i, props) : '---'
    } else if (
      (_.endsWith(item.path, 'TransportSenderDate') ||
        _.endsWith(item.path, 'TransportRecipientDate')) &&
      item.type === 'ADD' &&
      item.status === 'PENDING' &&
      props &&
      props.key === 'value'
    ) {
      return item.value ? renderDate(item, i, props) : '---'
    } else if (item.type === 'REMOVE' && item.status === 'PENDING') {
      if (_.startsWith(item.path, '$.resourceBookings')) {
        if (props && props.key === 'oldValue') {
          const resourceBooking =
            proving.resourceBookings &&
            proving.resourceBookings.find(
              (resBookings: any) =>
                resBookings.resource._id === item.value.resource
            )
          return resourceBooking ? resourceBooking.resource.title : '---'
        } else {
          return t('enum.ProvingChangeOperation.REMOVE')
        }
      } else if (_.startsWith(item.path, '$.materials')) {
        if (props && props.key === 'oldValue') {
          const label =
            proving.materials &&
            proving.materials.find(
              (materials: any) => materials.material._id === item.value.material
            )
          return label ? label.material.label : '---'
        } else {
          return t('enum.ProvingChangeOperation.REMOVE')
        }
      } else if (_.startsWith(item.path, '$.resourceRequirements')) {
        if (props && props.key === 'oldValue') {
          return t(
            `enum.resourceRequirementType.${item.value.resourceRequirementType}`
          )
        } else {
          return t('enum.ProvingChangeOperation.REMOVE')
        }
      } else if (_.startsWith(item.path, '$.vehicles')) {
        if (props && props.key === 'oldValue') {
          if (item && item.value && item.value.type === 'external') {
            return item.value.labelExternal
          } else if (item && item.value && item.value.unknown) {
            return t('model.proving.vehicle.vehicleUnknown.long')
          } else if (
            _.endsWith(item.path, 'TransportSenderDate') ||
            _.endsWith(item.path, 'TransportRecipientDate')
          ) {
            return '---'
          } else if (item && item.value && item.value.label) {
            return item.value.label
          } else {
            return '---'
          }
        } else {
          return t('enum.ProvingChangeOperation.REMOVE')
        }
      } else if (_.startsWith(item.path, '$.participant')) {
        if (props && props.key === 'oldValue') {
          if (
            (item.value.unknownPerson &&
              item.value.unknownPerson.isUnknown === true &&
              item.value.unknownPerson.personType === 'user') ||
            (item.value.unknownPerson &&
              item.value.unknownPerson.isUnknown === true &&
              item.value.unknownPerson.personType === 'external')
          ) {
            return t('model.proving.participant.participantUnknown')
          } else if (
            (item.value.unknownPerson &&
              item.value.unknownPerson.isUnknown === false &&
              item.value.unknownPerson.personType === 'user') ||
            (item.value.unknownPerson &&
              item.value.unknownPerson.isUnknown === false &&
              item.value.unknownPerson.personType === 'external')
          ) {
            return `${item.value.unknownPerson.surName}, ${item.value.unknownPerson.firstName}`
          } else if (
            item.value.unknownPerson &&
            item.value.unknownPerson.personType === 'worker'
          ) {
            return (
              item.value.percentage +
              '%, ' +
              item.value.unknownPerson.department
            )
          } else if (
            item.value.unknownPerson &&
            item.value.unknownPerson.personType === 'supplier'
          ) {
            return `${item.value.unknownPerson.surName}, ${item.value.unknownPerson.firstName} - ${item.value.unknownPerson.supplier}`
          } else if (
            item.path === '$.participants' &&
            props &&
            props.key.length === 24
          ) {
            return 'Teilnehmer'
          } else {
            let person

            // tslint:disable-next-line: no-unused-expression
            proving &&
              proving.participants &&
              proving.participants.forEach((p: any) => {
                if (
                  (p.person && p.person._id === item.value.person) ||
                  (p.person && p.person._id === item.value)
                ) {
                  person = p.person
                }
              })
            return (
              <UserPersona person={person as any} size={PersonaSize.size24} />
            )
          }
        } else {
          return t('enum.ProvingChangeOperation.REMOVE')
        }
      }
    } else {
      switch (item.$key) {
        // TODO: add all special cases (enums, ...)
        case 'annotation':
          if (props && props.key === 'value') {
            return item.value
          } else {
            return item.oldValue
          }
        case 'amount':
          if (props && props.key === 'value') {
            return item.value
          } else {
            return item.oldValue
          }
        case 'percentage':
          if (props && props.key === 'value') {
            if (item.value || item.value === 0) {
              return `${item.value} %`
            } else {
              return `---`
            }
          } else {
            if (item.oldValue || item.oldValue === 0) {
              return `${item.oldValue} %`
            } else {
              return `---`
            }
          }
        case 'flightType':
          return renderEnum(t, 'flight.flightType')(item, i, props) || '---'
        case 'accomodationType':
          return renderEnum(t, 'accomodationType')(item, i, props)
        case 'accomodation':
          return item.value
        case 'carType':
          return renderEnum(t, 'rentalCar')(item, i, props)
        case 'bookingStatus':
          return renderEnum(t, 'bookingStatus')(item, i, props)
        case 'status':
          if (item.$key !== 'undefined') {
            return renderEnum(t, 'resourceRequirementStatus')(item, i, props)
          }
          break
        case 'dateFrom':
          const dateFrom = renderDate(item, i, props)
          if (dateFrom === 'Invalid Date') {
            return renderDateFromTicks(item, i, props)
          }
          if (dateFrom === 'NaN.NaN.NaN') {
            return '---'
          }
          return dateFrom
        case 'nmf':
          return 'Nutzung Mobile Ladeinfrastruktur'
        case 'dateTo':
          const dateTo = renderDate(item, i, props)
          if (dateTo === 'Invalid Date') {
            return renderDateFromTicks(item, i, props)
          }
          if (dateTo === 'NaN.NaN.NaN') {
            return '---'
          }
          return dateTo
        case '0':
          return item.value?.hotel
        case '1':
          return item.value?.hotel
        case '2':
          return item.value?.hotel
        case '3':
          return item.value?.hotel
        case '4':
          return item.value?.hotel
        case 'transport':
          return renderEnum(t, 'transportType')(item, i, props)
        case 'undefined':
          if (
            _.includes(item.path, 'orderIdDistribution') &&
            props &&
            props.key === 'value'
          ) {
            return (
              <>
                {item.value.orderId && `Auftragsnummer: ${item.value.orderId}`}
                <br />
                {item.value.percentage && `Anteil: ${item.value.percentage} %`}
                <br />
                {item.value.annotation &&
                  `Beschreibung: ${item.value.annotation}`}
              </>
            )
          }
          if (
            _.includes(item.path, 'orderIdDistribution') &&
            props &&
            props.key === 'oldValue'
          ) {
            if (item.type !== 'ADD') {
              return (
                <>
                  {item.value.orderId &&
                    `Auftragsnummer: ${item.value.orderId}`}
                  <br />
                  {item.value.percentage &&
                    `Anteil: ${item.value.percentage} %`}
                  <br />
                  {item.value.annotation &&
                    `Beschreibung: ${item.value.annotation}`}
                </>
              )
            } else {
              return '---'
            }
          }
          break
        case 'transportType':
          const transport = renderEnum(t, 'transportType')(item, i, props)
          if (transport && transport.length) {
            return transport
          }
          break
        default:
          const value = renderDefault(item, i, props)
          switch (true) {
            case isNull(value) || isUndefined(value):
              return '---'
            case isEmpty(value):
              return '---'
            case isString(value) && isoDateRegex.test(value):
              return renderDate(item, i, props)
            case isString(value):
            case isNumber(value):
              return value
            case isBoolean(value):
              return renderYesNoIcon(item, i, props)
            default:
              return JSON.stringify(value)
          }
      }
    }
  }

export interface ProvingChangesListProps {
  provingChanges: ProvingChange[]
  onNavigateGroup: (group: IGroup) => void
  onAcceptChanges?: (changes: ProvingChange[]) => void
  onRejectChanges?: (changes: ProvingChange[]) => void
  listProps?: Partial<
    Omit<IDetailsListProps, 'items' | 'groups' | 'columns' | 'groupProps'>
  >
}

export const ProvingChangesList: React.FC<ProvingChangesListProps> = ({
  listProps,
  provingChanges,
  onNavigateGroup,
  onAcceptChanges,
  onRejectChanges,
}) => {
  const [proving] = useCurrentProving()
  const { t } = useTranslation(['ProvingChangeList', 'common'])
  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])
  const renderKey = React.useMemo(
    () => createRenderKey(proving, t),
    [proving, t]
  )
  const renderValue = React.useMemo(
    () => createRenderValue(proving, t),
    [proving, t]
  )
  const columns = React.useMemo(
    () => [
      fieldColumn('created', { onRender: renderTimeAgo }),
      fieldColumn('type', {
        onRender: renderEnum(t, 'ProvingChangeOperation'),
        isGrouped: true,
      }),
      fieldColumn('$key', {
        onRender: renderKey,
        minWidth: 200,
      }),
      fieldColumn('oldValue', { onRender: renderValue }),
      fieldColumn('value', { onRender: renderValue }),
    ],
    [fieldColumn, renderKey, renderValue, t]
  )

  const { groups, sortedItems } = React.useMemo(
    () =>
      groupSortByColumn(
        provingChanges!,
        false,
        fieldColumn('path', {
          isGrouped: true,
          getGroupValue: (item: any) => {
            if (startsWith(item.change.path, '$.participant')) {
              const participantId = item.change.path.split("'")[1]
              let person: any
              let personUnknown
              let personType
              let department
              let percentage
              let supplier
              let surName
              let firstName

              // tslint:disable-next-line: no-unused-expression
              proving &&
                proving.participants &&
                proving.participants.forEach((p) => {
                  if (p._id === participantId) {
                    if (p.unknownPerson && p.unknownPerson.isUnknown) {
                      personUnknown = true
                    }
                    if (p.unknownPerson && p.unknownPerson.personType) {
                      if (
                        p.unknownPerson.personType === 'user' &&
                        p.unknownPerson.surName &&
                        p.unknownPerson.firstName
                      ) {
                        personType = 'user'
                        surName = p.unknownPerson.surName
                        firstName = p.unknownPerson.firstName
                      } else if (p.unknownPerson.personType === 'user') {
                        personType = 'user'
                      } else if (p.unknownPerson.personType === 'worker') {
                        personType = 'worker'
                        department = p.unknownPerson.department
                        percentage = p.percentage
                      } else if (p.unknownPerson.personType === 'supplier') {
                        personType = 'supplier'
                        firstName = p.unknownPerson.firstName
                        surName = p.unknownPerson.surName
                        supplier = p.unknownPerson.supplier
                      }
                    }
                    if (p.person) {
                      firstName = _.upperFirst(p.person.firstName)
                      surName = _.upperFirst(p.person.lastName)
                      person = p.person
                    }
                  }
                })
              // let result = ''
              // result = item.change.path.split(']')[0]
              return person && personUnknown !== true
                ? `Person: ${surName}, ${person.firstName}`
                : personType === 'supplier'
                ? `Person: ${surName}, ${firstName} - ${supplier}`
                : personUnknown && personType === 'worker'
                ? `Person: ${percentage}%, ${department}`
                : person && personUnknown === false && personType === 'user'
                ? `Person: ${surName}, ${firstName}`
                : firstName && surName && personType === 'user'
                ? `Person: ${surName}, ${firstName}`
                : 'Teilnehmer'
            } else if (startsWith(item.change.path, '$.nmf')) {
              return 'Nutzung mobile Ladeinfrastruktur'
            } else if (startsWith(item.change.path, '$.specialUsage')) {
              return 'Streckennutzung zu besonderen Zeiten'
            } else if (startsWith(item.change.path, '$.vehicle')) {
              let vehicleName
              const vehicleId = item.change.path.split("'")[1]
              // tslint:disable-next-line: no-unused-expression
              proving &&
                proving.vehicles &&
                proving.vehicles.forEach((v) => {
                  if (v._id === vehicleId) {
                    if (v.type === 'internal' && !v.unknown) {
                      vehicleName = _.upperFirst(
                        (v as InternalVehicle).label || ''
                      )
                    } else if (v.type === 'external' && !v.unknown) {
                      vehicleName = _.upperFirst(
                        (v as ExternalVehicle).labelExternal || ''
                      )
                    } else if (v.type === 'internal' && v.unknown) {
                      vehicleName = `${_.upperFirst(
                        (v as InternalVehicle).model || ''
                      )} [Fahrzeug noch unbekannt]`
                    } else if (v.type === 'external' && v.unknown) {
                      vehicleName = `${_.upperFirst(
                        (v as ExternalVehicle).labelExternal || ''
                      )} [Fremdfahrzeug noch unbekannt]`
                    }
                  }
                })
              return `Fahrzeug: ${vehicleName || ''} `
            } else if (startsWith(item.change.path, '$.material')) {
              let materialName
              const materialId = item.change.path.split("'")[1]
              // tslint:disable-next-line: no-unused-expression
              proving &&
                proving.materials &&
                proving.materials.forEach((m) => {
                  if (m._id === materialId) {
                    materialName = m.material.label
                  }
                })
              return `Material: ${materialName || ''}`
            } else if (startsWith(item.change.path, '$.resourceRequirement')) {
              let resReqType
              const resReqId = item.change.path.split("'")[1]

              // tslint:disable-next-line: no-unused-expression
              proving &&
                proving.resourceRequirements &&
                proving.resourceRequirements.forEach((r) => {
                  if (r._id === resReqId) {
                    resReqType = r.resourceRequirementType
                  }
                })
              return resReqType
                ? 'Ressourcenbedarf: ' +
                    t(`enum.resourceRequirementType.${resReqType}`)
                : 'Ressourcenbedarf'
            } else {
              return _.includes(item.change.path, '[')
                ? item.change.path.split('[')[0].split('.')[1]
                : item.change.path.split('.')[1]
            }
          },
        }),
        columns[0]
      ),
    [provingChanges, columns, fieldColumn, proving, t]
  )

  const groupHeaderRenderer = React.useCallback<
    IRenderFunction<IDetailsGroupDividerProps>
  >(
    (renderProps) => (
      <GroupHeader
        {...renderProps}
        // onAcceptAllGroupChanges={group =>
        //   onAcceptChanges(sortedItems.slice(group.startIndex, group.count))
        // }
        // onRejectAllGroupChanges={group =>
        //   onRejectChanges(sortedItems.slice(group.startIndex, group.count))
        // }
        // onNavigateGroup={onNavigateGroup}
      />
    ),
    []
  )

  const items = React.useMemo(
    () =>
      sortedItems.map(({ change: { key, ...change }, ...rest }) => ({
        ...rest,
        ...change,
        $key: key,
      })),
    [sortedItems]
  )

  return (
    <StyledDetailsList
      layoutMode={DetailsListLayoutMode.justified}
      {...listProps}
      checkboxVisibility={CheckboxVisibility.always}
      groups={groups}
      columns={columns}
      groupProps={{ onRenderHeader: groupHeaderRenderer }}
      items={items}
    />
  )
}
